import React, { useCallback } from 'react';
import { TrendingUp, RefreshCw, AlertCircle, IndianRupee } from 'lucide-react';
import Table from '../components/Table';
import FilterBar from '../components/FilterBar';
import { useFetch, useFilters } from '../hooks';
import { labBookingService } from '../services/api';
import { formatDate, formatCurrency } from '../utils/helpers';

/* ── Status badge ───────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    requested:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    completed:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    cancelled:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    refunded:   'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
};

/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '', dateFrom: '', dateTo: '' };
const FILTER_FIELDS = [
  {
    key: 'status', label: 'Status', type: 'select',
    options: [
      { value: 'requested',  label: 'Requested'  },
      { value: 'completed',  label: 'Completed'  },
      { value: 'cancelled',  label: 'Cancelled'  },
      { value: 'refunded',   label: 'Refunded'   },
    ],
  },
  { key: 'dateFrom', label: 'From Date', type: 'date' },
  { key: 'dateTo',   label: 'To Date',   type: 'date' },
];

const filterFn = (b, f) => {
  if (f.search) {
    const q = f.search.toLowerCase();
    if (![b.booking_id, b.patient_name, b.lab_test_name, b.order_uid].join(' ').toLowerCase().includes(q)) return false;
  }
  if (f.status   && b.status        !== f.status)   return false;
  if (f.dateFrom && b.booking_date  <  f.dateFrom)  return false;
  if (f.dateTo   && b.booking_date  >  f.dateTo)    return false;
  return true;
};

/* ── Stat Card ──────────────────────────────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, iconBg, iconColor }) => (
  <div className="card p-4 flex items-center gap-3">
    <div className={`p-2.5 rounded-xl shrink-0 ${iconBg}`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="font-bold text-gray-900 dark:text-white">{value}</p>
    </div>
  </div>
);

/* ── Page ───────────────────────────────────────────────────────────────────── */
const LabPayments = () => {
  const { data: result, loading } = useFetch(() => labBookingService.getPayments());

  const payments = result?.payments || [];
  const summary  = result?.summary  || {};

  const filterFnCb = useCallback(filterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(
    FILTER_INITIAL, filterFnCb, payments
  );

  const columns = [
    {
      key: "booking_id",
      label: "Booking ID",
      render: (v, row) => (
        <div>
          <span className="font-mono text-xs font-semibold text-medihub-primary dark:text-medihub-highlight">
            #{v}
          </span>
          <p className="text-xs text-gray-400 mt-0.5">{row.order_uid}</p>
        </div>
      ),
    },
    {
      key: "patient_name",
      label: "Patient",
      render: (v, row) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white text-sm">
            {v}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {row.patient_mobile}
          </p>
        </div>
      ),
    },
    {
      key: "lab_test_name",
      label: "Test",
      render: (v) => (
        <p className="text-sm font-medium text-gray-900 dark:text-white max-w-[160px] truncate">
          {v}
        </p>
      ),
    },
    {
      key: "total_amount",
      label: "Amount",
      render: (v, row) => (
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">
            {formatCurrency(v)}
          </p>
          {parseFloat(row.total_discount) > 0 && (
            <p className="text-xs text-green-600 dark:text-green-400">
              -{formatCurrency(row.total_discount)} off
            </p>
          )}
        </div>
      ),
    },
    {
      key: "test_price",
      label: "Test Price",
      render: (v) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {formatCurrency(v)}
        </span>
      ),
    },
    {
      key: "booking_date",
      label: "Booking Date",
      render: (v) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {formatDate(v)}
        </span>
      ),
    },
    {
      key: "payment_method",
      label: "Payment method",
      render: (v) =><StatusBadge status={v} />
    },
    {
      key: "status",
      label: "Status",
      render: (v) => <StatusBadge status={v} />,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lab Payments</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Track and manage all lab test payment transactions</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={IndianRupee} label="Total Revenue"   value={formatCurrency(summary.total_revenue  || 0)} iconBg="bg-green-50 dark:bg-green-900/20"   iconColor="text-green-600 dark:text-green-400"   />
        <StatCard icon={TrendingUp}  label="Total Bookings"  value={summary.total_bookings || 0}                 iconBg="bg-blue-50 dark:bg-blue-900/20"     iconColor="text-blue-600 dark:text-blue-400"     />
        <StatCard icon={RefreshCw}   label="Total Discount"  value={formatCurrency(summary.total_discount || 0)} iconBg="bg-purple-50 dark:bg-purple-900/20" iconColor="text-purple-600 dark:text-purple-400" />
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={onFilterChange}
        onReset={onReset}
        searchPlaceholder="Search by booking ID, patient, test…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      {/* Table */}
      <Table
        title={`Lab Transactions (${filtered.length}${activeCount ? ` of ${payments.length}` : ''})`}
        columns={columns}
        data={filtered}
        loading={loading}
        exportFilename="lab-payments"
      />
    </div>
  );
};

export default LabPayments;
